/**
 * The cloud request at its plainest: the session cookie and a deadline, and the error that a
 * refused answer becomes.
 */

import { ApiError } from './apiError';

// Without a deadline, a dead relay or backend leaves a fetch hanging forever. A caller's
// init.signal overrides it.
export const REQUEST_TIMEOUT_MS = 15000;

// A proxy's HTML error page must not become a wall-of-text toast.
const MAX_ERROR_TEXT_LENGTH = 200;

/**
 * A request carrying the session cookie and the default deadline. A 401 is the caller's: the
 * sign-in flow asks with a credential that may be refused.
 */
export async function fetchWithCookie(url: string, init: RequestInit = {}): Promise<Response> {
    // The cloud API authenticates via the occurra_token HttpOnly cookie set by
    // /vms/api/v1/auth/session; credentials:'include' sends it on every request, including
    // same-origin Vite-proxied calls. No Authorization header is added: the server checks a
    // Bearer token against its own signing key, so the Google ID token rides only on the
    // sign-in requests that pass it themselves.
    return await fetch(url, {
        ...init,
        credentials: 'include',
        signal: init.signal ?? AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
}

/**
 * Builds the rejection for a failed response, preferring the server's own
 * explanation over a bare status line. The Go services answer errors as
 * `{"error": "..."}`, and that sentence is often the only thing that tells
 * the user WHY a write was refused — a 409 from enrollment naming, say, names
 * the object class that already owns the name. Callers surface `err.message`
 * in a toast, so throwing "HTTP 409" there hid the reason entirely.
 * `status` rides along for callers that branch on the code.
 */
export async function httpError(resp: Response): Promise<ApiError> {
    let message = '';
    let path = '';
    try {
        const text = (await resp.text()).trim();
        if (text.startsWith('{')) {
            const body: unknown = JSON.parse(text);
            const fields = body as { error?: unknown; message?: unknown; path?: unknown };
            if (typeof fields.error === 'string') {
                message = fields.error;
            } else if (typeof fields.message === 'string') {
                message = fields.message;
            }
            // The log settings endpoint names the offending field this way, which is
            // what lets a form show the server's complaint on the field itself.
            if (typeof fields.path === 'string') {
                path = fields.path;
            }
        } else if (text && !text.startsWith('<')) {
            message = text.slice(0, MAX_ERROR_TEXT_LENGTH);
        }
    } catch {
    // Unreadable or malformed body — fall back to the status below.
    }
    return new ApiError(message || `HTTP ${resp.status}`, resp.status, path || undefined);
}
